import React, { useEffect } from 'react';
import { ArrowLeft, FileText, Users, BarChart2, Layers, Target, CheckCircle, Brain, GitMerge, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import TableOfContents from '../components/TableOfContents';

const DesignMaturityCaseStudy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const sections = [
    { id: 'overview',         label: 'Overview' },
    { id: 'starting-state',   label: 'Starting State' },
    { id: 'framework',        label: 'The Framework' },
    { id: 'process',          label: 'Process Improvements' },
    { id: 'ai-craft',         label: 'Craft in AI' },
    { id: 'cross-functional', label: 'Cross-Functional' },
    { id: 'outcomes',         label: 'Outcomes' },
  ];

  const steps = [
    { id: 1, title: 'Structured Intake',    desc: 'Goals · Purpose · Use Cases',            icon: <FileText className="w-5 h-5 text-accent" />, detail: 'Shifted conversations from features to outcomes by standardizing requirements before design work started.' },
    { id: 2, title: 'Research & Alignment', desc: 'Brainstorming · Knowledge Base',          icon: <Users className="w-5 h-5 text-accent" />,    detail: 'Created a shared research knowledge base so insights were reusable across products and joiners.' },
    { id: 3, title: 'Design Execution',     desc: 'Flows · UI · Heuristic Alignment',        icon: <Layers className="w-5 h-5 text-accent" />,   detail: 'Moved beyond shipping interfaces to crafting strategic, heuristic-aligned user flows.' },
    { id: 4, title: 'Design System',        desc: 'Components · Patterns · Standards',       icon: <CheckCircle className="w-5 h-5 text-accent" />, detail: 'Transitioned from a component library to a full design system to ensure consistency and speed.' },
    { id: 5, title: 'Measurement',          desc: 'Usage Analytics · Funnels',               icon: <BarChart2 className="w-5 h-5 text-accent" />, detail: 'Built analytics dashboards to evaluate design decisions based on real user behavior.' },
    { id: 6, title: 'Accountability',       desc: 'KPIs · Benchmarks',                       icon: <Target className="w-5 h-5 text-accent" />,   detail: 'Attached KPIs to every new requirement, feeding learnings back into future designs.' },
  ];

  const aiPatterns = [
    {
      pattern: 'Model Confidence Display',
      icon: <Brain className="w-5 h-5 text-accent" />,
      challenge: 'AI output is probabilistic, but most UIs present it as certain. Users lose trust when the model is wrong and they had no signal it might be.',
      solution: 'Designed tiered confidence indicators — high confidence surfaced cleanly, lower confidence shown with visible uncertainty cues and a path to verify.',
    },
    {
      pattern: 'Correctable Suggestions',
      icon: <GitMerge className="w-5 h-5 text-accent" />,
      challenge: 'If AI output feels permanent or hard to edit, users either blindly accept errors or stop using the feature entirely.',
      solution: 'Made every AI-generated field directly editable inline. Corrections were visually distinct from original output so users always knew what they had changed.',
    },
    {
      pattern: 'Graceful Handoff to the User',
      icon: <RefreshCw className="w-5 h-5 text-accent" />,
      challenge: 'When the model is unsure, a blank or wrong output erodes trust fast — especially in clinical or enterprise contexts where errors have real consequences.',
      solution: 'Designed explicit "low confidence" states that handed control back to the user clearly — rather than guessing, the system surfaced the uncertainty and asked for input.',
    },
  ];

  return (
    <div className="bg-pastel-bg min-h-screen font-sans text-neutral-900 selection:bg-accent/20 relative">
      <TableOfContents sections={sections} />

      <nav className="fixed top-0 left-0 right-0 z-50 bg-pastel-bg/90 backdrop-blur-md">
        <div className="container mx-auto px-6 md:px-12 h-20 flex items-center">
          <Link to="/" className="flex items-center gap-2 text-neutral-600 hover:text-accent transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Back to Home</span>
          </Link>
        </div>
        <div className="h-px bg-neutral-200/60 mx-6 md:mx-12" />
      </nav>

      <main className="pt-32 pb-20">
        <div className="container mx-auto px-6 md:px-12 max-w-4xl">

          {/* 1. Header */}
          <header id="overview" className="mb-20 scroll-mt-32">
            <h1 className="text-4xl md:text-6xl font-serif text-neutral-900 mb-6 leading-tight">
              Building Design Maturity
            </h1>
            <p className="text-xl text-neutral-600 leading-relaxed max-w-3xl mb-10">
              Transforming design from a delivery step into a strategic, data-informed, and system-driven practice inside a fast-moving AI division.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-2xl border border-neutral-100">
                <h3 className="text-xs uppercase tracking-wider font-bold text-accent mb-2">Starting Point</h3>
                <p className="text-lg font-bold">First Designer</p>
                <p className="text-sm text-neutral-500">No established practice</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-neutral-100">
                <h3 className="text-xs uppercase tracking-wider font-bold text-accent mb-2">Products</h3>
                <p className="text-lg font-bold">4 AI Products</p>
                <p className="text-sm text-neutral-500">LLM + ML workflows</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-neutral-100">
                <h3 className="text-xs uppercase tracking-wider font-bold text-accent mb-2">My Role</h3>
                <p className="text-lg font-bold">Lead Designer</p>
                <p className="text-sm text-neutral-500">Practice + craft + leadership</p>
              </div>
            </div>
          </header>

          {/* 2. Starting State */}
          <section id="starting-state" className="mb-24 scroll-mt-32">
            <h2 className="text-3xl font-serif mb-8">Starting State</h2>
            <div className="bg-white p-8 rounded-3xl border border-neutral-100 mb-8">
              <p className="text-xl text-neutral-600 leading-relaxed mb-6">
                I joined as the <strong className="text-neutral-900">first designer in the Analytical AI division</strong>. There was no established design practice — design was treated as a delivery step, something that happened near the end to make things look good before shipping.
              </p>
              <p className="text-xl text-neutral-900 font-serif">
                My goal was to build design maturity within the team — not just ship interfaces.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                { label: 'What "design" meant when I joined', items: ['Wireframes requested when dev was almost done', 'No research informing decisions', 'No shared components or patterns', 'Success undefined — no metrics attached'] },
                { label: 'What I was building toward',        items: ['Design at the start of every problem', 'Research as an ongoing practice', 'System-level consistency across products', 'KPIs attached to every design decision'] },
              ].map((col, i) => (
                <div key={i} className={`p-6 rounded-2xl border ${i === 0 ? 'bg-red-50 border-red-100' : 'bg-green-50 border-green-100'}`}>
                  <h3 className={`font-bold mb-4 ${i === 0 ? 'text-red-800' : 'text-green-800'}`}>{col.label}</h3>
                  <ul className={`space-y-2 text-sm ${i === 0 ? 'text-red-700' : 'text-green-700'}`}>
                    {col.items.map((item, j) => <li key={j}>• {item}</li>)}
                  </ul>
                </div>
              ))}
            </div>
          </section>

          {/* 3. Framework */}
          <section id="framework" className="mb-24 scroll-mt-32">
            <div className="flex items-center justify-between mb-10">
              <h2 className="text-3xl font-serif">The Maturity Framework</h2>
              <div className="flex items-center gap-2 text-sm text-neutral-500 bg-white px-4 py-2 rounded-full border border-neutral-100">
                <RefreshCw className="w-4 h-4 text-accent" /> Continuous Loop
              </div>
            </div>

            <div className="relative">
              <div className="absolute top-1/2 left-0 w-full h-px bg-neutral-100 -z-10 hidden md:block" />
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {steps.map((step, index) => (
                  <motion.div
                    key={step.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.08 }}
                    viewport={{ once: true }}
                    className="bg-white p-5 rounded-2xl border border-neutral-100 shadow-sm flex flex-col h-full group hover:-translate-y-1 transition-transform duration-300"
                  >
                    <div className="bg-pastel-cream w-10 h-10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-accent/10 transition-colors">
                      {step.icon}
                    </div>
                    <div className="text-xs font-bold text-accent mb-1 uppercase tracking-wider">Step 0{step.id}</div>
                    <h3 className="font-bold text-neutral-900 mb-1 text-sm">{step.title}</h3>
                    <p className="text-xs text-neutral-500">{step.desc}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* 4. Process Improvements */}
          <section id="process" className="mb-24 scroll-mt-32">
            <h2 className="text-3xl font-serif mb-10">Process Improvements</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-8">
                <div>
                  <h3 className="text-xl font-serif mb-4 flex items-center gap-3">
                    <span className="bg-neutral-900 text-white w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0">1</span>
                    Structure at Entry
                  </h3>
                  <p className="text-neutral-600 leading-relaxed bg-white p-6 rounded-2xl border border-neutral-100 text-sm">
                    Introduced a <strong className="text-neutral-900">standardized requirement format</strong> that clarified goals, purpose, use cases, and success metrics before any design work started. This shifted conversations from "what should it look like" to "what outcome are we solving for."
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-serif mb-4 flex items-center gap-3">
                    <span className="bg-neutral-900 text-white w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0">2</span>
                    Research Embedded
                  </h3>
                  <p className="text-neutral-600 leading-relaxed bg-white p-6 rounded-2xl border border-neutral-100 text-sm">
                    Ran structured use-case discussions and usability studies, and built a <strong className="text-neutral-900">shared research knowledge base</strong> so findings were accessible to all designers and didn't live in individual docs.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-serif mb-4 flex items-center gap-3">
                    <span className="bg-neutral-900 text-white w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0">3</span>
                    Data-Driven Design
                  </h3>
                  <p className="text-neutral-600 leading-relaxed bg-white p-6 rounded-2xl border border-neutral-100 text-sm">
                    Introduced <strong className="text-neutral-900">quantitative usage analytics</strong> via Mixpanel — building funnel dashboards and usage flows to evaluate design decisions against real behavior, not assumptions.
                  </p>
                </div>
              </div>
              <div className="space-y-8 md:mt-16">
                <div>
                  <h3 className="text-xl font-serif mb-4 flex items-center gap-3">
                    <span className="bg-neutral-900 text-white w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0">4</span>
                    Scaling Consistency
                  </h3>
                  <p className="text-neutral-600 leading-relaxed bg-white p-6 rounded-2xl border border-neutral-100 text-sm">
                    As scale increased, inconsistency became a compounding risk. Transitioned the team from a component library to a <strong className="text-neutral-900">full design system</strong> — enabling standardization and reducing rework across 4 products.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-serif mb-4 flex items-center gap-3">
                    <span className="bg-neutral-900 text-white w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0">5</span>
                    Closing the Loop
                  </h3>
                  <p className="text-neutral-600 leading-relaxed bg-white p-6 rounded-2xl border border-neutral-100 text-sm">
                    Attached <strong className="text-neutral-900">KPIs to every new requirement</strong> — setting clear benchmarks upfront and feeding post-launch learnings back into the next design cycle. This made design outcomes legible to Product and leadership.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* 5. AI Craft */}
          <section id="ai-craft" className="mb-24 scroll-mt-32">
            <h2 className="text-3xl font-serif mb-4">Craft in AI</h2>
            <p className="text-lg text-neutral-600 mb-10 leading-relaxed">
              Designing for AI output required developing new interaction patterns that standard design systems don't cover — around trust, transparency, and user control.
            </p>
            <div className="space-y-6">
              {aiPatterns.map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-3xl border border-neutral-100 overflow-hidden"
                >
                  <div className="p-6 border-b border-neutral-100 flex items-center gap-3">
                    <div className="bg-pastel-cream w-9 h-9 rounded-xl flex items-center justify-center shrink-0">
                      {item.icon}
                    </div>
                    <h3 className="font-bold text-neutral-900">{item.pattern}</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-neutral-100">
                    <div className="p-6">
                      <span className="text-xs uppercase tracking-wider text-neutral-400 font-medium block mb-2">Challenge</span>
                      <p className="text-sm text-neutral-600 leading-relaxed">{item.challenge}</p>
                    </div>
                    <div className="p-6">
                      <span className="text-xs uppercase tracking-wider text-accent font-medium block mb-2">Solution</span>
                      <p className="text-sm text-neutral-700 leading-relaxed">{item.solution}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
            <div className="mt-8 bg-neutral-900 text-white p-8 rounded-2xl">
              <p className="text-lg italic leading-relaxed">
                "We ran usability studies and experiments to validate these patterns before scaling them across products — treating each pattern as a hypothesis until users confirmed it worked."
              </p>
            </div>
          </section>

          {/* 6. Cross-Functional */}
          <section id="cross-functional" className="mb-24 scroll-mt-32">
            <h2 className="text-3xl font-serif mb-8">Cross-Functional Work</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { team: 'ML Teams', desc: 'Partnered from the technical design stage — shaping how AI capabilities were built, not just how they were presented. Design input influenced what the model attempted, not just how output was displayed.' },
                { team: 'Product',  desc: 'Co-owned discovery and definition. Introduced requirement formats that embedded design criteria earlier in the planning cycle, reducing late-stage rework.' },
                { team: 'Engineering', desc: 'Established component contracts and token standards. Reduced design-to-dev translation friction and ensured the system in production matched the system in Figma.' },
              ].map((item, i) => (
                <div key={i} className="bg-white p-6 rounded-2xl border border-neutral-100">
                  <h3 className="font-bold text-neutral-900 mb-3">{item.team}</h3>
                  <p className="text-sm text-neutral-500 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* 7. Outcomes */}
          <section id="outcomes" className="scroll-mt-32">
            <div className="bg-accent/5 p-12 rounded-[2.5rem] border border-accent/10">
              <h2 className="text-2xl md:text-3xl font-serif text-neutral-900 mb-6">The Evolution</h2>
              <p className="text-xl text-neutral-700 max-w-2xl leading-relaxed mb-10">
                Over time, design evolved into a <span className="text-accent font-bold">scalable, data-informed, and system-driven practice</span> — one where design decisions were backed by research, measured against KPIs, and built on shared foundations.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                  { label: 'Research',      desc: 'Every product decision backed by user insights from a shared knowledge base' },
                  { label: 'Measurement',   desc: 'KPIs attached to requirements, analytics measuring outcomes post-launch' },
                  { label: 'Consistency',   desc: 'Shared design system and AI interaction patterns across 4 products' },
                ].map((item, i) => (
                  <div key={i} className="bg-white p-5 rounded-2xl border border-neutral-100">
                    <h3 className="font-bold text-neutral-900 mb-2">{item.label}</h3>
                    <p className="text-sm text-neutral-500 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

        </div>
      </main>
    </div>
  );
};

export default DesignMaturityCaseStudy;
